export type NotificationChannel = 'in_app' | 'email' | 'push';

export type CampaignType = 'trial_ending' | 'failed_payment' | 'new_feature' | 'custom';

export type CampaignStatus = 'draft' | 'sent' | 'cancelled';

export type CampaignAudience = 'all' | 'free' | 'pro' | 'enterprise' | 'inactive';

export type UserNotificationType = 'campaign' | 'system' | 'billing' | 'feature';

export interface NotificationChannelConfig {
  inAppEnabled: boolean;
  emailEnabled: boolean;
  pushEnabled: boolean;
  emailFromName?: string;
  emailReplyTo?: string;
  updatedAt?: string;
}

export const DEFAULT_NOTIFICATION_CHANNELS: NotificationChannelConfig = {
  inAppEnabled: true,
  emailEnabled: false,
  pushEnabled: false,
  emailFromName: 'LexGat',
};

export interface NotificationCampaign {
  id: string;
  type: CampaignType;
  title: string;
  body: string;
  channels: NotificationChannel[];
  targetAudience: CampaignAudience;
  status: CampaignStatus;
  sentCount?: number;
  inAppCount?: number;
  emailQueuedCount?: number;
  pushQueuedCount?: number;
  createdAt: string;
  sentAt?: string;
  createdBy?: string;
  createdByEmail?: string | null;
}

export interface UserNotification {
  id: string;
  userId: string;
  userEmail?: string;
  title: string;
  body: string;
  type: UserNotificationType;
  campaignId?: string;
  /** Shared id for all recipients of one in-app send (broadcast or single). */
  deliveryGroupId?: string;
  href?: string;
  read: boolean;
  readAt?: string;
  /** Set when the user dismisses the first-login banner for this notification. */
  bannerDismissedAt?: string;
  createdBy?: string;
  createdAt: string;
}

export interface UserNotificationPrefs {
  pushCourtDates: boolean;
  emailSummaries: boolean;
  marketingInApp: boolean;
}

export const DEFAULT_NOTIFICATION_PREFS: UserNotificationPrefs = {
  pushCourtDates: true,
  emailSummaries: false,
  marketingInApp: true,
};

export const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  trial_ending: 'Trial ending',
  failed_payment: 'Failed payment',
  new_feature: 'New feature',
  custom: 'Custom',
};

export const CAMPAIGN_AUDIENCE_LABELS: Record<CampaignAudience, string> = {
  all: 'All lawyers',
  free: 'Free plan',
  pro: 'Pro plan',
  enterprise: 'Enterprise plan',
  inactive: 'Inactive (30+ days)',
};

export const CAMPAIGN_TEMPLATES: Record<
  Exclude<CampaignType, 'custom'>,
  { title: string; body: string; channels: NotificationChannel[] }
> = {
  trial_ending: {
    title: 'Your LexGat trial ends soon',
    body: 'Upgrade to Pro to keep unlimited AI drafts and advanced templates.',
    channels: ['in_app', 'email'],
  },
  failed_payment: {
    title: 'Payment failed — action required',
    body: 'We could not process your last payment. Update your billing details to avoid interruption.',
    channels: ['in_app', 'email', 'push'],
  },
  new_feature: {
    title: 'New on LexGat',
    body: 'We shipped improvements to drafting and case management. Open LexGat to explore.',
    channels: ['in_app', 'push'],
  },
};
